import { SERVICE_MONTHS } from './constants.js';

const STORAGE_KEY = 'billing-reconciliation-service-months';

const localStorageSafe = () => {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage || null;
  } catch (_error) {
    return null;
  }
};

export const normalizeServiceMonth = (value) => {
  if (!value) return null;
  if (typeof value === 'string' && /^\d{4}-\d{2}-01$/.test(value)) return value;
  if (typeof value === 'string' && /^\d{4}-\d{2}$/.test(value)) return `${value}-01`;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  return `${year}-${month}-01`;
};

export const serviceMonthLabel = (monthId) => {
  const normalized = normalizeServiceMonth(monthId);
  if (!normalized) return '';
  const [year, month] = normalized.split('-');
  return new Date(Date.UTC(Number(year), Number(month) - 1, 1)).toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
};

export const mergeServiceMonths = (...groups) => {
  const byId = new Map();
  groups.flat().forEach((entry) => {
    const id = normalizeServiceMonth(typeof entry === 'string' ? entry : entry?.id);
    if (!id) return;
    byId.set(id, { id, label: entry?.label || serviceMonthLabel(id) });
  });
  return [...byId.values()].sort((left, right) => String(right.id).localeCompare(String(left.id)));
};

export const serviceMonthsFromRuns = (payload) => {
  const rows = Array.isArray(payload) ? payload : payload?.rows || [];
  return mergeServiceMonths(rows.map((run) => (
    run?.serviceMonth || run?.service_month || run?.monthId
  )));
};

const readStoredMonths = () => {
  const storage = localStorageSafe();
  if (!storage) return [];
  try {
    const parsed = JSON.parse(storage.getItem(STORAGE_KEY) || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch (_error) {
    return [];
  }
};

const writeStoredMonths = (months) => {
  const storage = localStorageSafe();
  if (!storage) return;
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(months.map(({ id }) => id)));
  } catch (_error) {
    // The selector still updates in memory when browser storage is unavailable.
  }
};

export const loadServiceMonthOptions = () => mergeServiceMonths(SERVICE_MONTHS, readStoredMonths());

export const persistServiceMonthOption = (monthId) => {
  const next = mergeServiceMonths(SERVICE_MONTHS, readStoredMonths(), [monthId]);
  writeStoredMonths(next.filter(({ id }) => !SERVICE_MONTHS.some((month) => month.id === id)));
  return next;
};
